import { useState } from 'react';
import { S1CloudProviderData } from 'common/graphqlTypes';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { CloudProviderField } from './types';
import { MAX_CLOSE_MULTIPLE_FIELDS_VALUES } from './consts';
import { getFieldValues } from './utils';

import './EndpointCloudProviderField.scss';

export type EndpointCloudProviderFieldProps = {
  cloudProviderField: CloudProviderField;
  s1CloudProvidersData: S1CloudProviderData;
};

/**
 * This component displays endpoint's cloud field.
 * a field can be from multiple values - the user can expand and see more then 2 results.
 *
 * @prop cloudProviderField current field configuration.
 * @prop s1CloudProvidersData sections cloud provider data.
 */
function EndpointCloudProviderField({
  cloudProviderField,
  s1CloudProvidersData,
}: EndpointCloudProviderFieldProps): JSX.Element {
  const [isOpenFieldsState, setOpenFieldsState] = useState<boolean>(false);
  const toggleOpenFieldState = () => setOpenFieldsState(!isOpenFieldsState);

  const createFieldValueContent = (values: string[], isOpenFieldsState: boolean): JSX.Element | JSX.Element[] => {
    if (values.length > 0) {
      return values.slice(0, isOpenFieldsState ? values?.length : MAX_CLOSE_MULTIPLE_FIELDS_VALUES).map((value, i) => (
        <div key={i}>
          <TruncateWithTooltip
            text={value || cloudProviderField.defaultValue}
            maxLength={77}
            id={cloudProviderField.key}
          />
        </div>
      ));
    }
    return <div key={0}>{cloudProviderField.defaultValue}</div>;
  };

  // unify data to an array.
  const values = getFieldValues(s1CloudProvidersData, cloudProviderField.key);
  const isViewMore = values && values.length > MAX_CLOSE_MULTIPLE_FIELDS_VALUES;
  return (
    <div className="EndpointCloudProviderField">
      <div className="EndpointCloudProviderField__Title">{cloudProviderField.title}</div>
      <div className="EndpointCloudProviderField__Value">
        <div className={'EndpointCloudProviderField__ValueContent' + (isOpenFieldsState ? ' open' : '')}>
          {createFieldValueContent(values, isOpenFieldsState)}
        </div>
        {isViewMore && (
          <Button
            className="EndpointCloudProviderField__ViewMore"
            onClick={() => toggleOpenFieldState()}
            variant={ButtonVariant.BORDERLESS}
            size="sm"
          >
            View {isOpenFieldsState ? 'less' : 'more'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default EndpointCloudProviderField;
